import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ToggleLeft, Plus, Pencil, Trash2, Loader2, RefreshCw, Globe2, Building2, X
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ENV_OPTIONS = ['preview', 'production'];

const emptyDraft = {
  key: '',
  label: '',
  description: '',
  category: 'in_development',
  enabled_global: false,
  enabled_for_clients: [],
  enabled_for_envs: [],
};

const FeatureFlags = () => {
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState([]);
  const [env, setEnv] = useState('unknown');
  const [tenants, setTenants] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // 'create' | 'edit'
  const [draft, setDraft] = useState(emptyDraft);
  const [tenantInput, setTenantInput] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ==================== DATA ====================

  const fetchFlags = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/feature-flags');
      setFlags(res.data?.flags || []);
      setEnv(res.data?.env || 'unknown');
    } catch (err) {
      toast.error('Failed to load feature flags', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/tenants');
      setTenants(res.data || []);
    } catch {
      // non-fatal — admin can still type client_id manually
    }
  }, [api]);

  useEffect(() => {
    fetchFlags();
    fetchTenants();
  }, [fetchFlags, fetchTenants]);

  // ==================== ACTIONS ====================

  const openCreate = () => {
    setEditorMode('create');
    setDraft(emptyDraft);
    setTenantInput('');
    setEditorOpen(true);
  };

  const openEdit = (flag) => {
    setEditorMode('edit');
    setDraft({
      key: flag.key,
      label: flag.label || '',
      description: flag.description || '',
      category: flag.category || 'in_development',
      enabled_global: !!flag.enabled_global,
      enabled_for_clients: flag.enabled_for_clients || [],
      enabled_for_envs: flag.enabled_for_envs || [],
    });
    setTenantInput('');
    setEditorOpen(true);
  };

  const toggleQuick = async (flag, field, value) => {
    try {
      await api.patch(`/api/super-admin/feature-flags/${flag.key}`, { [field]: value });
      toast.success(`Updated ${flag.key}`);
      fetchFlags();
    } catch (err) {
      toast.error('Update failed', { description: err.response?.data?.detail || err.message });
    }
  };

  const toggleEnv = (envKey) => {
    setDraft((prev) => {
      const has = prev.enabled_for_envs.includes(envKey);
      return {
        ...prev,
        enabled_for_envs: has
          ? prev.enabled_for_envs.filter((e) => e !== envKey)
          : [...prev.enabled_for_envs, envKey],
      };
    });
  };

  const addTenant = () => {
    const v = tenantInput.trim().toUpperCase();
    if (!v) return;
    setDraft((prev) => prev.enabled_for_clients.includes(v)
      ? prev
      : { ...prev, enabled_for_clients: [...prev.enabled_for_clients, v] });
    setTenantInput('');
  };

  const removeTenant = (clientId) => {
    setDraft((prev) => ({
      ...prev,
      enabled_for_clients: prev.enabled_for_clients.filter((c) => c !== clientId),
    }));
  };

  const saveDraft = async () => {
    if (!draft.key.trim() || !draft.label.trim()) {
      toast.error('Key and label are required');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(draft.key)) {
      toast.error('Key must be lowercase, numbers, underscores only');
      return;
    }

    setSaving(true);
    try {
      if (editorMode === 'create') {
        await api.post('/api/super-admin/feature-flags', draft);
        toast.success('Feature flag created');
      } else {
        // Edit mode: PATCH everything except key
        const { key, ...patch } = draft;
        await api.patch(`/api/super-admin/feature-flags/${key}`, patch);
        toast.success('Feature flag updated');
      }
      setEditorOpen(false);
      fetchFlags();
    } catch (err) {
      toast.error('Save failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/api/super-admin/feature-flags/${confirmDelete.key}`);
      toast.success(`Deleted ${confirmDelete.key}`);
      setConfirmDelete(null);
      fetchFlags();
    } catch (err) {
      toast.error('Delete failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setDeleting(false);
    }
  };

  // ==================== RENDER ====================

  const cardClasses = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const labelMuted = isDark ? 'text-slate-400' : 'text-gray-500';
  const textMain = isDark ? 'text-white' : 'text-gray-900';

  return (
    <div className="space-y-6" data-testid="feature-flags-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-3 ${textMain}`}>
            <ToggleLeft className="h-6 w-6 text-[#ffd108]" />
            Feature Flags
          </h1>
          <p className={`text-sm ${labelMuted}`}>
            Toggle in-development features on/off globally, per tenant, or per environment.
            <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              env === 'production' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'
            }`}>
              <Globe2 className="h-3 w-3" /> env: {env}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchFlags} data-testid="ff-refresh-btn">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="ff-create-btn">
            <Plus className="mr-2 h-4 w-4" /> New Flag
          </Button>
        </div>
      </div>

      {/* Resolution legend */}
      <div className={`rounded-lg border px-4 py-3 text-sm ${isDark ? 'bg-slate-800/30 border-slate-700 text-slate-300' : 'bg-blue-50 border-blue-200 text-gray-700'}`} data-testid="ff-legend">
        <p className={`font-semibold mb-1.5 ${textMain}`}>How flags resolve (checked in order):</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li><span className="font-medium text-green-400">Global ON</span> — flag is enabled for <span className="font-medium">every tenant in every environment</span>. Overrides all other rules.</li>
          <li><span className="font-medium text-blue-400">Environment match</span> — flag is enabled for <span className="font-medium">all tenants</span> in that environment (e.g. adding "production" turns it on for every production tenant).</li>
          <li><span className="font-medium text-amber-400">Client ID match</span> — flag is enabled for <span className="font-medium">only that specific tenant</span>, regardless of environment.</li>
        </ol>
        <p className={`mt-1.5 ${labelMuted} text-xs`}>If none of the above match, the flag is OFF for that tenant. First matching rule wins.</p>
      </div>

      <Card className={cardClasses}>
        <CardHeader>
          <CardTitle className={textMain}>All Flags</CardTitle>
          <CardDescription className={labelMuted}>
            A flag resolves to ON if `enabled_global` is true, OR if the current env is in `enabled_for_envs`,
            OR if the caller's tenant is in `enabled_for_clients`. Otherwise it's OFF.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
            </div>
          ) : flags.length === 0 ? (
            <div className={`text-center py-12 ${labelMuted}`}>
              No feature flags yet. Click <strong>New Flag</strong> to add one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                  <TableHead className={labelMuted}>Key / Label</TableHead>
                  <TableHead className={labelMuted}>Global</TableHead>
                  <TableHead className={labelMuted}>Envs</TableHead>
                  <TableHead className={labelMuted}>Tenants</TableHead>
                  <TableHead className={labelMuted}>Updated</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {flags.map((flag) => (
                  <TableRow
                    key={flag.key}
                    className={isDark ? 'border-slate-700' : 'border-gray-100'}
                    data-testid={`ff-row-${flag.key}`}
                  >
                    <TableCell>
                      <div className={`font-mono text-sm font-semibold ${textMain}`}>{flag.key}</div>
                      <div className={`text-xs ${labelMuted}`}>{flag.label}</div>
                      {flag.description && (
                        <div className={`text-xs mt-1 ${labelMuted} max-w-md`}>{flag.description}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!flag.enabled_global}
                        onCheckedChange={(v) => toggleQuick(flag, 'enabled_global', v)}
                        data-testid={`ff-toggle-global-${flag.key}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(flag.enabled_for_envs || []).map((e) => (
                          <Badge key={e} variant="outline" className="text-xs">{e}</Badge>
                        ))}
                        {(flag.enabled_for_envs || []).length === 0 && (
                          <span className={`text-xs ${labelMuted}`}>—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(flag.enabled_for_clients || []).slice(0, 4).map((c) => (
                          <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                        ))}
                        {(flag.enabled_for_clients || []).length > 4 && (
                          <Badge variant="outline" className="text-xs">+{flag.enabled_for_clients.length - 4}</Badge>
                        )}
                        {(flag.enabled_for_clients || []).length === 0 && (
                          <span className={`text-xs ${labelMuted}`}>—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={`text-xs ${labelMuted}`}>
                      {flag.updated_at ? formatDistanceToNow(new Date(flag.updated_at), { addSuffix: true }) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(flag)}
                          data-testid={`ff-edit-btn-${flag.key}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmDelete(flag)}
                          data-testid={`ff-delete-btn-${flag.key}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-lg`} data-testid="ff-editor-dialog">
          <DialogHeader>
            <DialogTitle className={textMain}>
              {editorMode === 'create' ? 'New Feature Flag' : `Edit: ${draft.key}`}
            </DialogTitle>
            <DialogDescription className={labelMuted}>
              {editorMode === 'create'
                ? 'Define a new flag. Backend handlers can require it via Depends(require_feature("key")).'
                : 'Toggle scope or update labels. Backend cache invalidates on save.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {editorMode === 'create' && (
              <div>
                <Label className={labelMuted}>Key (lowercase, _)</Label>
                <Input
                  value={draft.key}
                  onChange={(e) => setDraft((p) => ({ ...p, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                  placeholder="e.g. notify_operator"
                  data-testid="ff-key-input"
                />
              </div>
            )}

            <div>
              <Label className={labelMuted}>Label</Label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
                placeholder="Notify Operator (push job details)"
                data-testid="ff-label-input"
              />
            </div>

            <div>
              <Label className={labelMuted}>Description</Label>
              <Textarea
                value={draft.description || ''}
                onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                placeholder="Where the flag applies and why it's gated."
                data-testid="ff-description-input"
              />
            </div>

            <div>
              <Label className={labelMuted}>Category</Label>
              <Input
                value={draft.category}
                onChange={(e) => setDraft((p) => ({ ...p, category: e.target.value }))}
                placeholder="in_development"
                data-testid="ff-category-input"
              />
            </div>

            <div className="flex items-center justify-between rounded border border-slate-700 p-3">
              <div>
                <div className={textMain}>Global</div>
                <div className={`text-xs ${labelMuted}`}>Master switch — when on, the flag is on for everyone, ignoring env / tenant.</div>
              </div>
              <Switch
                checked={draft.enabled_global}
                onCheckedChange={(v) => setDraft((p) => ({ ...p, enabled_global: v }))}
                data-testid="ff-enabled-global-toggle"
              />
            </div>

            <div>
              <Label className={labelMuted}>Environments</Label>
              <div className="flex gap-2 mt-1">
                {ENV_OPTIONS.map((e) => {
                  const active = draft.enabled_for_envs.includes(e);
                  return (
                    <button
                      type="button"
                      key={e}
                      onClick={() => toggleEnv(e)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        active
                          ? 'bg-[#ffd108] border-[#ffd108] text-black'
                          : isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`ff-env-toggle-${e}`}
                    >
                      {e}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className={labelMuted}>Tenant allowlist (Client IDs)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tenantInput}
                  onChange={(e) => setTenantInput(e.target.value)}
                  placeholder="TST001"
                  list="ff-tenants-list"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTenant(); } }}
                  data-testid="ff-tenant-input"
                />
                <datalist id="ff-tenants-list">
                  {tenants.map((t) => (
                    <option key={t.client_id} value={t.client_id}>{t.company_name || t.client_id}</option>
                  ))}
                </datalist>
                <Button type="button" variant="outline" onClick={addTenant}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {draft.enabled_for_clients.map((c) => (
                  <Badge key={c} variant="outline" className="flex items-center gap-1 text-xs">
                    <Building2 className="h-3 w-3" /> {c}
                    <button
                      type="button"
                      onClick={() => removeTenant(c)}
                      className="ml-1 hover:text-red-400"
                      aria-label={`Remove ${c}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button
              onClick={saveDraft}
              disabled={saving}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              data-testid="ff-save-btn"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-md`}>
          <DialogHeader>
            <DialogTitle className={textMain}>Delete feature flag?</DialogTitle>
            <DialogDescription className={labelMuted}>
              <code>{confirmDelete?.key}</code> will be removed permanently. Any backend route gated on it will return 404 to all callers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="ff-confirm-delete-btn"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureFlags;
